import { useEffect, useRef } from 'react'
import { PENDING_REQUESTS_EVENT } from '@/lib/dashboard-events'
import { notifyNewOrders, stopTitleFlash } from '@/lib/order-alerter'

/**
 * Listens to the global PENDING_REQUESTS_EVENT and triggers cross-cutting
 * alerts (sound + browser notification + title flash) whenever the count
 * grows. The first observed count seeds the baseline so we don't alert
 * for orders that already existed when the user loaded the app.
 */
export function useNewOrderAlerts() {
  const baselineRef = useRef<number | null>(null)

  useEffect(() => {
    const handle = (event: Event) => {
      const detail = (event as CustomEvent<{ count?: number }>).detail
      if (typeof detail?.count !== 'number') return

      const next = detail.count

      if (baselineRef.current == null) {
        baselineRef.current = next
        return
      }

      const delta = next - baselineRef.current
      if (delta > 0) {
        notifyNewOrders(delta)
      }

      // If pending count drops to zero, clear any title-flash state.
      if (next === 0) {
        stopTitleFlash()
      }

      baselineRef.current = next
    }

    window.addEventListener(PENDING_REQUESTS_EVENT, handle as EventListener)
    return () => {
      window.removeEventListener(PENDING_REQUESTS_EVENT, handle as EventListener)
      stopTitleFlash()
    }
  }, [])
}
